import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    FileText,
    Download,
    Eye,
    Calendar,
    CheckCircle,
    XCircle,
    Search,
    ArrowUpDown,
    Globe,
    ArrowLeft,
    Loader2,
    Pencil,
    Archive,
    ArchiveRestore,
    Tag,
} from 'lucide-react';
import toast from 'react-hot-toast';
import TechnicalReportViewer from '../components/reports/TechnicalReportViewer';
import EditMetadataModal from '../components/common/EditMetadataModal';
import { reportsApi, getErrorMessage } from '../lib/api';

export default function ReportsPage() {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'name' | 'status' | 'size'>('newest');
    const [showArchived, setShowArchived] = useState(false);
    const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
    const [reports, setReports] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editingReport, setEditingReport] = useState<any | null>(null);
    const [actioningId, setActioningId] = useState<string | null>(null);

    // Fetch reports from API
    const fetchReports = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await reportsApi.list({ page: 1, page_size: 100, archived: showArchived });
            setReports(data.items || []);
        } catch (err: any) {
            console.error('Error loading reports:', err);
            setError(getErrorMessage(err) || t('reports.errorLoading'));
        } finally {
            setLoading(false);
        }
    }, [showArchived, t]);

    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    // Client-side search + sort over the fetched page
    const displayedReports = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        let list = reports;
        if (q) {
            list = list.filter((r) =>
                [r.title, r.polygon_name, r.certificate_id, r.label, r.notes, r.compliance_status]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase()
                    .includes(q),
            );
        }
        const ts = (d: string) => (d ? new Date(d).getTime() : 0);
        return [...list].sort((a, b) => {
            switch (sortBy) {
                case 'oldest':
                    return ts(a.created_at) - ts(b.created_at);
                case 'name':
                    return (a.title || '').localeCompare(b.title || '');
                case 'status':
                    return (a.compliance_status || '').localeCompare(b.compliance_status || '');
                case 'size':
                    return (b.file_size_bytes || 0) - (a.file_size_bytes || 0);
                case 'newest':
                default:
                    return ts(b.created_at) - ts(a.created_at);
            }
        });
    }, [reports, searchQuery, sortBy]);

    const formatFileSize = (bytes: number) => {
        const mb = bytes / (1024 * 1024);
        return `${mb.toFixed(1)} MB`;
    };

    const handleDownload = async (reportId: string, certificateId: string) => {
        try {
            const filename = `EUDR_Report_${certificateId}.pdf`;
            await reportsApi.download(reportId, filename);
        } catch (err: any) {
            console.error('Error downloading report:', err);
            toast.error(t('reports.downloadError'));
        }
    };

    const handleEditSave = async ({ label, notes }: { label: string; notes: string }) => {
        if (!editingReport) return;
        try {
            await reportsApi.update(editingReport.id, { label, notes });
            setReports((prev) =>
                prev.map((r) => (r.id === editingReport.id ? { ...r, label, notes } : r)),
            );
            toast.success(t('manage.saved'));
        } catch (err: any) {
            toast.error(getErrorMessage(err) || t('manage.actionError'));
            throw err; // keep the modal open on failure
        }
    };

    const handleArchiveToggle = async (report: any) => {
        const next = !report.is_archived;
        const message = next ? t('manage.archiveConfirm') : t('manage.unarchiveConfirm');
        if (!window.confirm(message)) return;
        try {
            setActioningId(report.id);
            await reportsApi.update(report.id, { is_archived: next });
            // The current view shows either active or archived reports, so the
            // toggled report leaves the visible set either way.
            setReports((prev) => prev.filter((r) => r.id !== report.id));
            toast.success(next ? t('manage.archivedSuccess') : t('manage.restoredSuccess'));
        } catch (err: any) {
            toast.error(getErrorMessage(err) || t('manage.actionError'));
        } finally {
            setActioningId(null);
        }
    };

    // If a report is selected, show the technical viewer
    if (selectedReportId) {
        return (
            <div className="space-y-6 animate-fade-in">
                <button
                    onClick={() => setSelectedReportId(null)}
                    className="flex items-center gap-2 text-primary-600 hover:text-primary-700 transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                    <span>{t('reports.backToList')}</span>
                </button>
                <TechnicalReportViewer reportId={selectedReportId} />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {t('reports.title')}
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400">
                        {t('reports.subtitle')}
                    </p>
                </div>
            </div>

            {/* Search and filters */}
            <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={t('manage.searchReports')}
                        className="input pl-12 w-full"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                            className="input pl-9 pr-8 appearance-none"
                            aria-label={t('manage.sortBy')}
                        >
                            <option value="newest">{t('manage.sortNewest')}</option>
                            <option value="oldest">{t('manage.sortOldest')}</option>
                            <option value="name">{t('manage.sortName')}</option>
                            <option value="status">{t('manage.sortStatus')}</option>
                            <option value="size">{t('manage.sortSize')}</option>
                        </select>
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowArchived((v) => !v)}
                        className={`btn-secondary flex items-center gap-2 whitespace-nowrap ${showArchived ? 'ring-2 ring-primary-500' : ''}`}
                    >
                        {showArchived ? <ArchiveRestore className="w-5 h-5" /> : <Archive className="w-5 h-5" />}
                        {showArchived ? t('manage.showActive') : t('manage.showArchived')}
                    </button>
                </div>
            </div>

            {/* Loading state */}
            {loading && (
                <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                        <Loader2 className="w-12 h-12 animate-spin text-primary-600 mx-auto mb-4" />
                        <p className="text-gray-600 dark:text-gray-400">{t('reports.loadingReports')}</p>
                    </div>
                </div>
            )}

            {/* Error state */}
            {error && (
                <div className="card p-6">
                    <div className="flex items-start gap-3">
                        <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                {t('reports.loadError')}
                            </h3>
                            <p className="text-red-600">{error}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Empty state */}
            {!loading && !error && displayedReports.length === 0 && (
                <div className="card p-8 sm:p-12 text-center">
                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        {searchQuery.trim()
                            ? t('manage.noResults')
                            : showArchived
                            ? t('manage.noArchived')
                            : t('reports.noReports')}
                    </h3>
                    {!searchQuery.trim() && !showArchived && (
                        <p className="text-gray-500">{t('reports.autoGenerated')}</p>
                    )}
                </div>
            )}

            {/* Reports grid */}
            {!loading && !error && displayedReports.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {displayedReports.map((report) => (
                    <div key={report.id} className="card-hover p-6">
                        {/* Header */}
                        <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-xl bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 flex items-center justify-center">
                                    <FileText className="w-6 h-6" />
                                </div>
                            </div>
                            <div className="flex items-center gap-1">
                                <span
                                    className={
                                        report.compliance_status === 'compliant'
                                            ? 'badge-success'
                                            : 'badge-danger'
                                    }
                                >
                                    {report.compliance_status === 'compliant' ? (
                                        <CheckCircle className="w-3 h-3 mr-1" />
                                    ) : (
                                        <XCircle className="w-3 h-3 mr-1" />
                                    )}
                                    {report.compliance_status === 'compliant'
                                        ? t('compliance.compliant')
                                        : t('compliance.nonCompliant')}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setEditingReport(report)}
                                    title={t('manage.editReport')}
                                    aria-label={t('manage.editReport')}
                                    className="p-2 sm:p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200 transition-colors"
                                >
                                    <Pencil className="w-4 h-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleArchiveToggle(report)}
                                    disabled={actioningId === report.id}
                                    title={report.is_archived ? t('manage.unarchive') : t('manage.archive')}
                                    aria-label={report.is_archived ? t('manage.unarchive') : t('manage.archive')}
                                    className="p-2 sm:p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200 transition-colors disabled:opacity-50"
                                >
                                    {actioningId === report.id ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : report.is_archived ? (
                                        <ArchiveRestore className="w-4 h-4" />
                                    ) : (
                                        <Archive className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Content */}
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                            {report.title}
                        </h3>
                        {report.label && (
                            <div className="mb-2">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                                    <Tag className="w-3 h-3" />
                                    {report.label}
                                </span>
                            </div>
                        )}
                        <p className="text-sm text-gray-500 mb-4">{report.polygon_name}</p>

                        {/* Details */}
                        <div className="space-y-2 text-sm mb-4">
                            <div className="flex items-center justify-between">
                                <span className="text-gray-500">{t('reports.certificate')}</span>
                                <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                                    {report.certificate_id}
                                </code>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-gray-500">{t('reports.language')}</span>
                                <span className="flex items-center gap-1">
                                    <Globe className="w-3 h-3" />
                                    {report.language === 'es' ? 'Español' : 'English'}
                                </span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-gray-500">{t('common.date')}</span>
                                <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {new Date(report.created_at).toLocaleDateString()}
                                </span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-gray-500">{t('reports.fileSize')}</span>
                                <span>{formatFileSize(report.file_size_bytes)}</span>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 pt-4 border-t border-gray-100 dark:border-gray-700">
                            <button
                                onClick={() => setSelectedReportId(report.id)}
                                className="btn-secondary flex-1 flex items-center justify-center gap-2 py-2"
                            >
                                <Eye className="w-4 h-4" />
                                {t('reports.viewAnalysis')}
                            </button>
                            <button
                                onClick={() => handleDownload(report.id, report.certificate_id)}
                                className="btn-primary flex-1 flex items-center justify-center gap-2 py-2"
                            >
                                <Download className="w-4 h-4" />
                                PDF
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            )}

            {/* Edit metadata modal */}
            {editingReport && (
                <EditMetadataModal
                    title={t('manage.editReport')}
                    subtitle={editingReport.title || editingReport.polygon_name || undefined}
                    initialLabel={editingReport.label}
                    initialNotes={editingReport.notes}
                    onSave={handleEditSave}
                    onClose={() => setEditingReport(null)}
                />
            )}
        </div>
    );
}
